import { useEffect, RefObject, useCallback } from 'react'

/**
 * Hook to detect clicks outside of a referenced element.
 * Commonly used for closing dropdowns, modals, and inline edit fields.
 *
 * @param ref - React ref to the element to monitor
 * @param callback - Function to call when a click outside is detected
 * @param enabled - Whether the listener is active (default: true)
 *
 * @example
 * ```tsx
 * const wrapperRef = useRef<HTMLDivElement>(null)
 * useClickOutside(wrapperRef, () => setIsEditing(false), isEditing)
 * ```
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  callback: () => void,
  enabled: boolean = true
): void {
  const stableCallback = useCallback(callback, [callback])

  useEffect(() => {
    if (!enabled) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is inside the ref
      if (ref.current && ref.current.contains(target)) {
        return
      }

      // Check if click is inside a Radix portal (for Select, Dialog, etc.)
      // Radix portals are appended to body and have data-radix-portal attribute
      const targetElement = target as HTMLElement
      if (targetElement.closest?.('[data-radix-portal]') ||
          targetElement.closest?.('[data-radix-select-content]') ||
          targetElement.closest?.('[data-slot="select-content"]')) {
        return
      }

      stableCallback()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [ref, stableCallback, enabled])
}
